import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail } from "lucide-react";
import { BRAND, PUBLIC_MEDIA } from "../../lib/constants";

/**
 * BookingCTA — full-bleed final section. Edge-to-edge cinematic image background
 * with a left-aligned editorial copy block + 3 CTAs (Book / Quote / Call). Brand-warm.
 */
export default function BookingCTA() {
  return (
    <section
      data-testid="booking-cta"
      className="relative w-screen min-h-[78vh] md:min-h-[88vh] flex items-end overflow-hidden bg-[#0A0A0A] text-[#F5F2EB]"
    >
      <img
        src={`${PUBLIC_MEDIA("theo-oppolia/public/photos/opp_3.webp")}?w=1600`}
        srcSet={`${PUBLIC_MEDIA("theo-oppolia/public/photos/opp_3.webp")}?w=800 800w, ${PUBLIC_MEDIA("theo-oppolia/public/photos/opp_3.webp")}?w=1200 1200w, ${PUBLIC_MEDIA("theo-oppolia/public/photos/opp_3.webp")}?w=1600 1600w`}
        sizes="100vw"
        alt=""
        width="1920"
        height="1280"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover opacity-65"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/15" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full px-6 sm:px-10 md:px-16 lg:px-24 xl:px-32 pb-16 md:pb-24 pt-32 md:pt-40 grid md:grid-cols-12 gap-10"
      >
        <div className="md:col-span-7">
          <p className="overline text-[#B08858] tracking-[0.22em]">Begin the project</p>
          <h2 className="font-display leading-[1.02] mt-5 text-5xl md:text-7xl lg:text-8xl tracking-tight">
            Free<br />
            <em className="not-italic text-[#B08858]">design consultation.</em>
          </h2>
          <p className="mt-8 max-w-2xl text-[#F5F2EB]/85 text-base md:text-xl leading-relaxed">
            Share your floor plan. Our senior designer returns a 3D concept and a transparent factory-direct quote within 24 hours — yours to keep, no obligation.
          </p>

          {/* Inclusions checklist — reinforces the offer at the moment of decision */}
          <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 max-w-2xl" data-testid="booking-cta-included">
            {[
              "Space-optimization software",
              "Custom design",
              "3D rendering",
              "Production",
              "Coordinated delivery",
              "Installation",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-[#F5F2EB]/90 text-sm md:text-base">
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" className="shrink-0">
                  <path d="M2 7l3.5 3.5L12 3" stroke="#B08858" strokeWidth="2.2" fill="none" strokeLinecap="square" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-5 md:flex md:flex-col md:justify-end gap-4">
          <Link to="/consultation" data-testid="booking-cta-consult" className="btn-sharp primary w-full md:w-auto justify-center group">
            Book Free Consultation
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/products" data-testid="booking-cta-quote" className="btn-sharp outline-light w-full md:w-auto justify-center">
            Request a Quote
          </Link>
          <Link to="/showroom" data-testid="booking-cta-showroom" className="btn-sharp outline-light w-full md:w-auto justify-center gap-2 flex-wrap">
            <span>Explore the Showroom</span>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[#B08858]/80">Virtual · Physical opening soon</span>
          </Link>
          <div className="mt-6 pt-6 border-t border-white/15 flex flex-col gap-3 text-sm">
            <a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-3 text-[#F5F2EB]/75 hover:text-[#B08858] transition" data-testid="booking-cta-phone">
              <Phone size={14} className="text-[#B08858]" />
              <span>
                {BRAND.phone}
                <span className="block text-[10px] tracking-[0.18em] uppercase text-[#F5F2EB]/45">General</span>
              </span>
            </a>
            <a href={`tel:${BRAND.phoneSalesRaw}`} className="flex items-center gap-3 text-[#F5F2EB]/75 hover:text-[#B08858] transition" data-testid="booking-cta-phone-sales">
              <Phone size={14} className="text-[#B08858]" />
              <span>
                {BRAND.phoneSales}
                <span className="block text-[10px] tracking-[0.18em] uppercase text-[#F5F2EB]/45">Sales</span>
              </span>
            </a>
            <a href={`mailto:${BRAND.emailSales}`} className="flex items-center gap-3 text-[#F5F2EB]/75 hover:text-[#B08858] transition" data-testid="booking-cta-email-sales">
              <Mail size={14} className="text-[#B08858]" />
              <span>
                {BRAND.emailSales}
                <span className="block text-[10px] tracking-[0.18em] uppercase text-[#F5F2EB]/45">Quotes & sales</span>
              </span>
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
